import React, { useState, useEffect, forwardRef } from 'react';
import {
  customPropertyLevelService,
  customPropertyTypeService,
  customPropertyObjectMapService,
  searchIndexTypeService,
} from '../services';
import {
  Checkbox,
  Select,
  SelectButtons,
  formatSelectOptions,
  Input,
  TextareaAutosize,
} from '../components';
import { getBatchEditValue, CUSTOM_PROPERTY_OBJECT_MAP } from '../helpers';
import { useGlobalState, useMessage } from '../hooks';

const _AddCustomPropertyForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    custom_property_id: null,
    custom_property_name: '',
    custom_property_code: '',
    custom_property_description: '',
    custom_property_level_id: '',
    custom_property_type_id: '',
  };

  const initialBatchEditItems = {
    batch_custom_property_level_id: false,
    batch_custom_property_type_id: false,
    batch_custom_property_object_map: false,
    batch_search_index_type_id: false,
  };

  const [displayNames] = useGlobalState('displayNames');
  const [formData, setFormData] = useState(initialFormState);
  const [customPropertyTypes, setCustomPropertyTypes] = useState([]);
  const [customPropertyLevels, setCustomPropertyLevels] = useState([]);
  const [customPropertyObjectMaps, setCustomPropertyObjectMaps] = useState({});
  const [searchIndexTypes, setSearchIndexTypes] = useState([]);
  const [formObjectMap, setFormObjectMap] = useState([]);
  const [formObjectMapDelta, setFormObjectMapDelta] = useState({});
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);

  const { showMessage, hideMessage } = useMessage();

  useEffect(() => {
    // Get form data
    fetchCustomPropertyLevels();
    fetchCustomPropertyTypes();
    fetchCustomPropertyObjectMap();
    fetchSearchIndexTypes();
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        custom_property_id: data[0].custom_property_id,
        custom_property_name: data[0].custom_property_name || '',
        custom_property_code: data[0].custom_property_code || '',
        custom_property_description: data[0].custom_property_description || '',
        custom_property_level_id: data[0].custom_property_level_id || '',
        custom_property_type_id: data[0].custom_property_type_id || '',
        search_index_type_id: data[0].search_index_type_id || '',
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        custom_property_level_id: getBatchEditValue(data, 'custom_property_level_id'),
        custom_property_type_id: getBatchEditValue(data, 'custom_property_type_id'),
        search_index_type_id: getBatchEditValue(data, 'search_index_type_id'),
      });
    } else if (mode === 'add') {
      const defaultCustomPropertyLevel = customPropertyLevels?.find(
        x => x.custom_property_level_code === 'main'
      )?.custom_property_level_id;

      const defaultSearchIndexType = searchIndexTypes?.find(
        x => x.search_index_type_code === 'none'
      )?.search_index_type_id;

      setFormData(prev => ({
        ...prev,
        custom_property_level_id: defaultCustomPropertyLevel || '',
        search_index_type_id: defaultSearchIndexType || '',
      }));
    } else {
      resetForm();
    }
  }, [mode, data, customPropertyLevels, searchIndexTypes]);

  const fetchCustomPropertyLevels = async () => {
    customPropertyLevelService
      .getAll()
      .then(res => {
        res && setCustomPropertyLevels(res.custom_property_levels);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchCustomPropertyTypes = async () => {
    customPropertyTypeService
      .getAll()
      .then(res => {
        res && setCustomPropertyTypes(res.custom_property_types);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchSearchIndexTypes = async () => {
    searchIndexTypeService
      .getAll()
      .then(res => {
        res && setSearchIndexTypes(res.search_index_types);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchCustomPropertyObjectMap = async () => {
    const customPropertyIds = data.map(x => x.custom_property_id).join(',');

    if (customPropertyIds.length > 0) {
      customPropertyObjectMapService
        .getByCustomPropertyId(customPropertyIds)
        .then(res => {
          if (res) {
            // Batch edit
            if (data.length > 1 && mode !== 'add') {
              // Build display value with partial items
              let displayValue = [];
              CUSTOM_PROPERTY_OBJECT_MAP.forEach(object => {
                const items = res.custom_property_object_map.filter(
                  x => object.value === x.object_name
                );
                if (items.length === data.length) {
                  displayValue.push({ value: object.value });
                } else if (items.length > 0) {
                  displayValue.push({ value: object.value, partial: true });
                }
              });
              setFormObjectMap(displayValue);

              // Save per-CA individual maps as arrays for use at submit time
              let tmp = {};
              res.custom_property_object_map.forEach(object => {
                const prev = tmp[object.custom_property_id] || [];
                tmp[object.custom_property_id] = [...prev, object.object_name];
              });
              setCustomPropertyObjectMaps(tmp);

              // Add or single edit
            } else {
              let tmp = res.custom_property_object_map.map(x => ({ value: x.object_name }));
              setFormObjectMap(tmp);
            }
          }
        })
        .catch(err => {
          showMessage(err, 'error');
        });
    }
  };

  const handleObjectMapChange = ({ value: val }) => {
    setFormObjectMap(prev => {
      const exists = prev.find(x => x.value === val);
      if (exists) {
        if (batchEdit) setFormObjectMapDelta(prev => ({ ...prev, [val]: false }));
        return prev.filter(x => x.value !== val);
      } else {
        if (batchEdit) setFormObjectMapDelta(prev => ({ ...prev, [val]: true }));
        return [...prev, { value: val }];
      }
    });
  };

  const handleSelectAllObjectMap = () => {
    const fullySelected = formObjectMap.filter(x => !x.partial);
    if (fullySelected.length === CUSTOM_PROPERTY_OBJECT_MAP.length) {
      // Deselect all
      if (batchEdit) {
        const delta = {};
        CUSTOM_PROPERTY_OBJECT_MAP.forEach(o => (delta[o.value] = false));
        setFormObjectMapDelta(delta);
      }
      setFormObjectMap([]);
    } else {
      // Select all
      if (batchEdit) {
        const delta = {};
        CUSTOM_PROPERTY_OBJECT_MAP.forEach(o => (delta[o.value] = true));
        setFormObjectMapDelta(delta);
      }
      setFormObjectMap(CUSTOM_PROPERTY_OBJECT_MAP.map(o => ({ value: o.value })));
    }
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        // Restore the individual object map for this CA and apply the delta
        const individualMap = customPropertyObjectMaps[item.custom_property_id] || [];
        let objectMap = [...individualMap];
        Object.entries(formObjectMapDelta).forEach(([name, added]) => {
          if (added && !objectMap.includes(name)) objectMap.push(name);
          else if (!added) objectMap = objectMap.filter(x => x !== name);
        });

        return {
          custom_property_id: item.custom_property_id,
          custom_property_name: item.custom_property_name,
          custom_property_code: item.custom_property_code,
          custom_property_description: item.custom_property_description,
          custom_property_level_id: item.custom_property_level_id,
          custom_property_type_id: item.custom_property_type_id,
          search_index_type_id: item.search_index_type_id,
          ...tmp,
          objectMap,
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        objectMap: formObjectMap.map(x => x.value),
      };
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);

    const defaultCustomPropertyLevel = customPropertyLevels?.find(
      x => x.custom_property_level_code === 'main'
    )?.custom_property_level_id;

    const defaultSearchIndexType = searchIndexTypes?.find(
      x => x.search_index_type_code === 'none'
    )?.search_index_type_id;

    setFormData({
      ...initialFormState,
      custom_property_level_id: defaultCustomPropertyLevel,
      search_index_type_id: defaultSearchIndexType,
    });
    setFormObjectMap([]);
    setFormObjectMapDelta({});
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="custom_property_name"
              label="Name"
              value={formData.custom_property_name}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.custom_property_name
                  ? { valid: false, message: formErrors.custom_property_name }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="custom_property_code"
              label="Code"
              value={formData.custom_property_code}
              onChange={handleInputChange}
              title={displayNames?.custom_property_code?.display_name_description}
              disabled={loading}
              validation={
                formErrors.custom_property_code
                  ? { valid: false, message: formErrors.custom_property_code }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <TextareaAutosize
              label="Description"
              name="custom_property_description"
              value={formData.custom_property_description}
              title={displayNames?.custom_property_description?.display_name_description}
              onChange={handleInputChange}
              disabled={loading}
              isOptional={true}
            />
          </div>
        )}

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_custom_property_type_id}
                name="batch_custom_property_type_id"
                label="Edit Type"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Select
            name="custom_property_type_id"
            label={!batchEdit && 'Type'}
            value={
              formData.custom_property_type_id === 'multiple'
                ? undefined
                : formData.custom_property_type_id
            }
            options={formatSelectOptions({
              options: customPropertyTypes,
              optionValue: 'custom_property_type_id',
              optionLabel: 'custom_property_type_name',
              optionSecondaryLabel: 'custom_property_type_form_control',
              optionIcon: 'custom_property_type_code',
              tooltip: 'custom_property_type_description',
            })}
            onChange={handleInputChange}
            title={displayNames?.custom_property_type_name?.display_name_description}
            placeholder={
              formData.custom_property_type_id === 'multiple'
                ? '< Multiple >'
                : 'Select Custom Property Type...'
            }
            disabled={loading || (batchEdit && !batchEditItems.batch_custom_property_type_id)}
            validation={
              formErrors.custom_property_type_id
                ? { valid: false, message: formErrors.custom_property_type_id }
                : undefined
            }
          />
        </div>

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_custom_property_object_map}
                name="batch_custom_property_object_map"
                label="Edit Asset Type Map"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Select
            name="custom_property_object_map"
            value={formObjectMap}
            options={CUSTOM_PROPERTY_OBJECT_MAP}
            onChange={handleObjectMapChange}
            onSelectAll={handleSelectAllObjectMap}
            label={!batchEdit && 'Asset Type Map'}
            title={displayNames?.custom_property_object_map?.display_name_description}
            isMulti={true}
            disabled={loading || (batchEdit && !batchEditItems.batch_custom_property_object_map)}
            sortOptions={false}
            isOptional={true}
          />
        </div>

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                value={batchEditItems.batch_custom_property_level_id}
                name="batch_custom_property_level_id"
                label="Edit Level"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <SelectButtons
            name="custom_property_level_id"
            label={!batchEdit && 'Level'}
            value={formData.custom_property_level_id}
            options={formatSelectOptions({
              options: customPropertyLevels,
              optionValue: 'custom_property_level_id',
              optionLabel: 'custom_property_level_name',
              tooltip: 'custom_property_level_description',
            })}
            onChange={handleInputChange}
            title={displayNames?.custom_property_level_name?.display_name_description}
            disabled={loading || (batchEdit && !batchEditItems.batch_custom_property_level_id)}
          />
        </div>

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                value={batchEditItems.batch_search_index_type_id}
                name="batch_search_index_type_id"
                label="Edit Search Index"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <SelectButtons
            name="search_index_type_id"
            label={!batchEdit && 'Search Index'}
            value={formData.search_index_type_id}
            options={formatSelectOptions({
              options: searchIndexTypes,
              optionValue: 'search_index_type_id',
              optionLabel: 'search_index_type_name',
              tooltip: 'search_index_type_description',
            })}
            onChange={handleInputChange}
            title={displayNames?.search_index_type_name?.display_name_description}
            disabled={loading || (batchEdit && !batchEditItems.batch_search_index_type_id)}
          />
        </div>
      </form>
    </div>
  );
};

export const AddCustomPropertyForm = forwardRef(_AddCustomPropertyForm);
